// src/pages/admin/InvoiceDetail.js
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, doc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../lib/firebase";

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function computeTotals({ items, discount, shipping, tax }) {
  const safeItems = Array.isArray(items) ? items : [];

  const itemsSubtotal = round2(
    safeItems.reduce((sum, it) => {
      const qty = num(it.qty, 0);
      const unit = num(it.unitPrice, 0);
      return sum + qty * unit;
    }, 0)
  );

  let discountAmount = 0;
  if (discount?.type === "percent") {
    discountAmount = round2(itemsSubtotal * (clamp(num(discount.value, 0), 0, 100) / 100));
  } else {
    discountAmount = round2(num(discount?.value, 0));
  }
  discountAmount = clamp(discountAmount, 0, itemsSubtotal);

  const afterDiscount = round2(Math.max(0, itemsSubtotal - discountAmount));

  let shippingAmount = 0;
  if (shipping?.type === "percent") {
    shippingAmount = round2(afterDiscount * (clamp(num(shipping.value, 0), 0, 100) / 100));
  } else {
    shippingAmount = round2(num(shipping?.value, 0));
  }
  shippingAmount = Math.max(0, shippingAmount);

  const preTaxSubtotal = round2(afterDiscount + shippingAmount);

  let taxAmount = 0;
  if (tax?.type === "percent") {
    taxAmount = round2(preTaxSubtotal * (clamp(num(tax.value, 0), 0, 100) / 100));
  } else {
    taxAmount = round2(num(tax?.value, 0));
  }
  taxAmount = Math.max(0, taxAmount);

  const grandTotal = round2(preTaxSubtotal + taxAmount);

  return {
    itemsSubtotal,
    discountAmount,
    shippingAmount,
    preTaxSubtotal,
    taxAmount,
    grandTotal,
  };
}

function getMillis(ts) {
  if (!ts) return 0;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.getTime();
}

function formatAddress(addr) {
  if (!addr) return "-";
  const parts = [
    addr.line1,
    addr.line2,
    addr.city,
    addr.region,
    addr.postalCode,
    addr.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const [allInvoices, setAllInvoices] = useState([]);

  useEffect(() => {
    if (!id) return;

    const ref = doc(db, "quotes", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const q = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setInvoice(q);
        setLoading(false);
      },
      () => {
        setInvoice(null);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [id]);

  useEffect(() => {
    const ref = collection(db, "quotes");
    const qy = query(ref, orderBy("createdAt", "desc"), limit(500));
    const unsub = onSnapshot(
      qy,
      (snap) => setAllInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setAllInvoices([])
    );

    return () => unsub();
  }, []);

  const related = useMemo(() => {
    if (!invoice) return [];
    const data = allInvoices.filter((r) => r.docType === "invoice");
    const clientId = invoice.clientId || null;

    let rows = data;
    if (clientId) {
      rows = rows.filter((r) => r.clientId === clientId);
    } else if (invoice.clientName) {
      const name = invoice.clientName.trim().toLowerCase();
      rows = rows.filter((r) => (r.clientName || "").toLowerCase() === name);
    }

    rows = rows.filter((r) => r.id !== invoice.id);
    rows.sort((a, b) => getMillis(b.createdAt) - getMillis(a.createdAt));
    return rows;
  }, [invoice, allInvoices]);

  if (loading) return <p className="muted">Loading...</p>;

  if (!invoice) {
    return (
      <div>
        <p className="muted">Invoice not found.</p>
        <Link className="btn" to="/admin/invoices">
          Back
        </Link>
      </div>
    );
  }

  const totals = invoice.totals || computeTotals({
    items: invoice.items,
    discount: invoice.discount,
    shipping: invoice.shipping,
    tax: invoice.tax,
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>{invoice.quoteNumber || "Invoice"}</h2>
          <div className="micro muted">Read-only</div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link className="btn primary" to={`/admin/invoices/${invoice.id}/edit`}>
            Edit
          </Link>
          <Link className="btn" to="/admin/invoices">
            Back
          </Link>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <h3 style={{ marginBottom: 8 }}>Client</h3>
        <div className="micro muted">{invoice.clientName || "Client"}</div>
        {invoice.clientOrganization && <div className="micro muted">{invoice.clientOrganization}</div>}
        <div className="micro muted">{formatAddress(invoice.clientAddress)}</div>
        {(invoice.clientEmail || invoice.clientPhone) && (
          <div className="micro muted">
            {[invoice.clientEmail, invoice.clientPhone].filter(Boolean).join(" | ")}
          </div>
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        <h3 style={{ marginBottom: 8 }}>Items</h3>
        {Array.isArray(invoice.items) && invoice.items.length > 0 ? (
          <div style={{ display: "grid", gap: 12 }}>
            {invoice.items.map((it, idx) => (
              <article
                key={idx}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 14,
                  padding: 12,
                  background: "rgba(255,255,255,0.7)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <strong>{it.name || `Item #${idx + 1}`}</strong>
                  <div className="micro muted">
                    {num(it.qty, 0)} x {money(num(it.unitPrice, 0))}
                  </div>
                </div>
                {it.desc && <div className="micro muted" style={{ marginTop: 6 }}>{it.desc}</div>}
              </article>
            ))}
          </div>
        ) : (
          <p className="muted">No items on this invoice.</p>
        )}
      </div>

      <div
        style={{
          marginTop: 18,
          border: "1px solid rgba(0,0,0,0.1)",
          borderRadius: 14,
          padding: 14,
          background: "rgba(255,255,255,0.7)",
          maxWidth: 520,
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: 10 }}>Totals</h4>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="muted">Items Subtotal</span>
            <span>{money(totals.itemsSubtotal)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="muted">Discount</span>
            <span>- {money(totals.discountAmount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="muted">Shipping</span>
            <span>{money(totals.shippingAmount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="muted">Pre-Tax Subtotal</span>
            <span>{money(totals.preTaxSubtotal)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="muted">Tax</span>
            <span>{money(totals.taxAmount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 6, fontSize: 18 }}>
            <span>Total</span>
            <span>{money(totals.grandTotal)}</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <h3 style={{ marginBottom: 8 }}>Other invoices for this client</h3>
        {related.length === 0 && <p className="muted">No other invoices.</p>}
        {related.length > 0 && (
          <div style={{ display: "grid", gap: 10 }}>
            {related.map((r) => (
              <article
                key={r.id}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 14,
                  padding: 12,
                  background: "rgba(255,255,255,0.7)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <strong>{r.quoteNumber || "Invoice"}</strong>
                    <div className="micro muted" style={{ marginTop: 4 }}>
                      {money(r?.totals?.grandTotal ?? 0)}
                    </div>
                  </div>
                  <Link className="btn" to={`/admin/invoices/${r.id}`}>
                    View
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
